import { Period, PeriodAnchor, ViewRange } from './types';

export const DAY = 86400000;

// The same category color is used on cards, anchors and interval tracks.
export const kindColor = (kind: string): string =>
  ({
    shock: '#ad4c19',
    trend: '#21627e',
    reversal: '#7250a2',
    correction: '#9b5d18',
    range: '#42715e',
    drawdown: '#a33e59',
    regime: '#596c87',
    volatility: '#817021',
  })[kind] || '#526580';

export const periodWindow = (period: Period) => ({
  min: Date.parse(period.display_window?.start_date || period.start_date),
  max: Date.parse(period.display_window?.end_date || period.end_date),
});

export const intersects = (period: Period, range: ViewRange | null) =>
  !range ||
  (periodWindow(period).min <= range.max &&
    periodWindow(period).max >= range.min);

export const anchorFor = (period: Period): PeriodAnchor =>
  period.primary_anchor || {
    date: period.peak_date || period.start_date,
    role: 'legacy_anchor',
    label: 'Detection anchor',
  };

export const visibleMarkers = (
  periods: Period[],
  selected: Period | null,
  range: ViewRange,
  limit = 8,
) => {
  const candidates = periods.filter(period => intersects(period, range));
  const chosen = candidates.slice(0, limit);
  if (
    selected &&
    intersects(selected, range) &&
    !chosen.some(period => period.id === selected.id)
  )
    chosen.push(selected);
  return chosen;
};

export type PeriodRelationship = {
  period: Period;
  kind: 'similar' | 'contains' | 'inside' | 'overlaps';
  overlapDays: number;
  sharedFraction: number;
};

/** Keep nested scales separate; only comparable windows form similar views. */
export const relatedPeriods = (
  selected: Period,
  periods: Period[],
): PeriodRelationship[] => {
  const left = periodWindow(selected);
  return periods
    .filter(period => period.id !== selected.id && intersects(period, left))
    .map(period => {
      const right = periodWindow(period);
      const overlap = Math.max(
        0,
        Math.min(left.max, right.max) - Math.max(left.min, right.min),
      );
      const span = Math.max(
        DAY,
        Math.max(left.max, right.max) - Math.min(left.min, right.min),
      );
      const sharedFraction = overlap / span;
      const selectedScale = selected.analytical_scale?.window_sessions;
      const otherScale = period.analytical_scale?.window_sessions;
      const comparableScale =
        selectedScale && otherScale
          ? Math.max(selectedScale, otherScale) /
              Math.min(selectedScale, otherScale) <=
            1.1
          : !selected.horizon ||
            !period.horizon ||
            selected.horizon === period.horizon;
      const sameMethod = Boolean(
        selected.detector?.method &&
          selected.detector.method === period.detector?.method,
      );
      const kind: PeriodRelationship['kind'] =
        sharedFraction >= 0.75 &&
        (sameMethod || (selected.kind === period.kind && comparableScale))
          ? 'similar'
          : right.min <= left.min && right.max >= left.max
            ? 'contains'
            : right.min >= left.min && right.max <= left.max
              ? 'inside'
              : 'overlaps';
      return {
        period,
        kind,
        overlapDays: Math.round(overlap / DAY),
        sharedFraction,
      };
    })
    .sort(
      (a, b) =>
        b.sharedFraction - a.sharedFraction || b.period.score - a.period.score,
    );
};
